import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { Offer } from '../../../services/exports/Interfaces';
import { selectActiveOffers, selectUnlockedOffers } from '../../../store/slices/OfferSlice';
import { selectHasCachedOrderPromoCode } from '../../../store/slices/OrderSlice';
import { selectGiftCard } from '../../../store/slices/GiftCardSlice';
import {
  ALL_PRODUCTS_PREVIEWABLE_OFFER_KINDS,
  OFFERS_DISCOUNTED_PRODUCTS_KEYS_BY_KIND,
  PREVIEWABLE_OFFER_KINDS,
  UNLOCKED_ONLY_PREVIEWABLE_OFFER_KINDS,
} from '../consts';

export interface PreviewableOffers {
  // In `activeOffers` order, which useWatchActiveOffers has already sorted by SORT_ORDER_BY_TYPE.
  // That order is the chaining order for the handlers' `apply`.
  offers: Offer[];
  // Products whose price one of `offers` would actually change. Lets a card skip the whole preview
  // with a single Set lookup — on a large menu almost every card lands here.
  discountedProductIds: Set<number>;
  // True when an offer discounts every line regardless of product, so no id filter applies.
  isEveryProductDiscounted: boolean;
}

const EMPTY_PREVIEWABLE_OFFERS: PreviewableOffers = Object.freeze({
  offers: [],
  discountedProductIds: new Set<number>(),
  isEveryProductDiscounted: false,
});

// A module-level selector, so this is derived once per store state and shared by every card that
// subscribes. Deriving it inside the per-card hook instead would rebuild the Set once per card.
export const selectPreviewableOffers = createSelector(
  [selectActiveOffers, selectUnlockedOffers, selectGiftCard, selectHasCachedOrderPromoCode],
  (activeOffers, unlockedOffers, giftCard, hasPromoCode): PreviewableOffers => {
    // Exclusivity model (CTO-confirmed 2026-08-03): a promo code or gift card is exclusive with
    // everything, so either one suppresses offers. An offer and a points redemption CAN be
    // combined, so points do NOT suppress offers.
    if (giftCard || hasPromoCode) {
      return EMPTY_PREVIEWABLE_OFFERS;
    }

    const unlockedIds = new Set((unlockedOffers ?? []).map((offer) => offer.id));

    // Filtered in place rather than merged, so the SORT_ORDER_BY_TYPE ordering survives.
    const offers = (activeOffers ?? []).filter(
      (offer) =>
        PREVIEWABLE_OFFER_KINDS.includes(offer?.kind) ||
        (UNLOCKED_ONLY_PREVIEWABLE_OFFER_KINDS.includes(offer?.kind) && unlockedIds.has(offer?.id)),
    );

    if (!offers.length) {
      return EMPTY_PREVIEWABLE_OFFERS;
    }

    const discountedProductIds = new Set<number>();

    offers.forEach((offer) =>
      (OFFERS_DISCOUNTED_PRODUCTS_KEYS_BY_KIND[offer.kind] ?? []).forEach((key) =>
        (offer.details?.[key] ?? []).forEach((id: number) => discountedProductIds.add(id)),
      ),
    );

    return {
      offers,
      discountedProductIds,
      isEveryProductDiscounted: offers.some((offer) => ALL_PRODUCTS_PREVIEWABLE_OFFER_KINDS.includes(offer.kind)),
    };
  },
);

export default function usePreviewableOffers(): PreviewableOffers {
  return useSelector(selectPreviewableOffers);
}
